import { useState } from 'react';
import { XCircle, CheckCircle, AlertCircle, Package } from 'lucide-react';
import './CancelOrder.css';

export default function CancelOrder() {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cancelledOrder, setCancelledOrder] = useState(null);

  // const API_URL = process.env.REACT_APP_API_URL;
  const API_URL = "https://backend-wellshape.vercel.app"

  
  const cancellationReasons = [
    'Found a better price elsewhere',
    'Changed my mind',
    'Ordered by mistake',
    'Delivery time is too long',
    'Need to change delivery address',
    'Product no longer needed',
    'Other'
  ];

  const handleCancel = async () => {
    setError('');

    // Validation
    if (!orderId.trim()) {
      setError('Please enter your Order ID');
      return;
    }

    if (!email.trim() || !phone.trim()) {
      setError('Please enter both email and phone number');
      return;
    }

    if (!reason) {
      setError('Please select a reason for cancellation');
      return;
    }

    if (reason === 'Other' && !customReason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/cancellation/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId: orderId,
          email: email,
          phone: phone,
          reason: reason,
          customReason: reason === 'Other' ? customReason : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit cancellation request');
      }

      if (data.success) {
        setCancelledOrder({
          orderId: data.data.orderId,
          cancellationId: data.cancellationId,
          email: email,
          phone: phone,
          reason: reason === 'Other' ? customReason : reason,
          status: data.data.status,
          cancellationDate: new Date(data.data.submittedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
        });
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit cancellation request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setOrderId('');
    setEmail('');
    setPhone('');
    setReason('');
    setCustomReason('');
    setError('');
    setSuccess(false);
    setCancelledOrder(null);
  };

  return (
    <div className="cancel-order-container">
      <div className="cancel-order-wrapper">
        <div className="cancel-order-header">
          <h1>Cancel Your Order</h1>
          <p>We're sorry to see you go. Please fill in the details below to cancel your order</p>
        </div>

        {!success ? (
          <div className="cancel-order-card">
            {error && (
              <div className="error-message">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <div className="cancel-form">
              {/* Order ID */}
              <div className="input-group">
                <label>Order ID <span className="required">*</span></label>
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="e.g., ORD-123456"
                  className="input-field"
                />
                <span className="input-hint">You can find your Order ID in the confirmation email</span>
              </div>

              {/* Email and Phone in Grid */}
              <div className="input-grid">
                <div className="input-group">
                  <label>Email Address <span className="required">*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="input-field"
                  />
                </div>

                <div className="input-group">
                  <label>Phone Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+92 300 1234567"
                    className="input-field"
                  />
                </div>
              </div>

              {/* Reason for Cancellation */}
              <div className="input-group">
                <label>Reason for Cancellation <span className="required">*</span></label>
                <div className="reason-options">
                  {cancellationReasons.map((reasonOption) => (
                    <label key={reasonOption} className="reason-option">
                      <input
                        type="radio"
                        name="reason"
                        value={reasonOption}
                        checked={reason === reasonOption}
                        onChange={(e) => setReason(e.target.value)}
                      />
                      <span>{reasonOption}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Custom Reason Textarea */}
              {reason === 'Other' && (
                <div className="input-group">
                  <label>Please specify your reason <span className="required">*</span></label>
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Tell us why you want to cancel this order..."
                    className="textarea-field"
                    rows="4"
                  />
                </div>
              )}

              {/* Warning Box */}
              <div className="warning-box">
                <AlertCircle size={20} />
                <div>
                  <h3>Important Information</h3>
                  <ul>
                    <li>Orders can only be cancelled if they haven't been shipped yet</li>
                    <li>Your request will be reviewed by our team</li>
                    <li>You will receive a confirmation email once the cancellation is processed</li>
                    <li>If approved, refunds will be processed within 5-7 business days</li>
                  </ul>
                </div>
              </div>

              {/* Buttons */}
              <div className="button-group">
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="cancel-btn"
                >
                  {loading ? 'PROCESSING...' : 'SUBMIT CANCELLATION REQUEST'}
                </button>
                <button
                  onClick={resetForm}
                  className="back-btn"
                  disabled={loading}
                >
                  GO BACK
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="success-container">
            <div className="success-card">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>

              <h2>Cancellation Request Submitted</h2>
              <p className="success-message">
                Your cancellation request has been submitted successfully. Our team will review it and send you a confirmation email shortly.
              </p>

              <div className="cancellation-details">
                <div className="detail-row">
                  <span className="detail-label">Request ID:</span>
                  <span className="detail-value">{cancelledOrder.cancellationId}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Order ID:</span>
                  <span className="detail-value">{cancelledOrder.orderId}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{cancelledOrder.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{cancelledOrder.phone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Request Date:</span>
                  <span className="detail-value">{cancelledOrder.cancellationDate}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Reason:</span>
                  <span className="detail-value">{cancelledOrder.reason}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value status-pending">{cancelledOrder.status.toUpperCase()}</span>
                </div>
              </div>

              <div className="refund-info">
                <Package size={24} />
                <div>
                  <h3>What Happens Next?</h3>
                  <div className="refund-details">
                    <p>✓ Our team will review your cancellation request</p>
                    <p>✓ You'll receive an email notification about the decision</p>
                    <p>✓ If approved, refund will be processed within 5-7 business days</p>
                    <p>✓ You can check your request status anytime using your Order ID</p>
                  </div>
                </div>
              </div>

              <button onClick={resetForm} className="new-cancellation-btn">
                Submit Another Request
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}